import type { ReactNode } from 'react'
import { AlertTriangle, Info, XOctagon } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog'
import { cn } from '@/shared/lib/utils'
import type { UrgentLevel } from './UrgentBanner'

const levelIcon: Record<UrgentLevel, typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  error: XOctagon,
}

const levelIconClass: Record<UrgentLevel, string> = {
  info: 'text-[var(--v2-brand)]',
  warning: 'text-[var(--v2-warn)]',
  error: 'text-[var(--v2-err)]',
}

/**
 * Communication urgente critique en modale - DESIGN §8 (RV-31). S'appuie sur AlertDialog
 * (Radix : `role=alertdialog`, focus trap, Escape). Bloquant : un seul bouton d'accusé de
 * réception (pas de fermeture passive pour les messages critiques). Libellés i18n par l'appelant.
 */
export function UrgentModal({
  open,
  onAcknowledge,
  level = 'error',
  title,
  message,
  acknowledgeLabel,
  className,
}: {
  open: boolean
  onAcknowledge: () => void
  level?: UrgentLevel
  title: ReactNode
  message: ReactNode
  acknowledgeLabel: string
  className?: string
}) {
  const Icon = levelIcon[level]

  return (
    <AlertDialog open={open}>
      <AlertDialogContent className={className}>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <Icon className={cn('size-5 shrink-0', levelIconClass[level])} aria-hidden />
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction
            onClick={onAcknowledge}
            className={cn(
              level === 'error' && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
            )}
          >
            {acknowledgeLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
